import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCustomerGiftCardBalance } from '../../services/gift-cards';
import useUser from '../global/useUser';
import { CheckoutCustomer } from '../../types/payments';
import { testPhoneNumbervalue, toE164 } from '../../utils/global/helper';

function useGetCustomerGiftCardBalance({
  customer,
  enabled = true,
}: {
  customer: Partial<Pick<CheckoutCustomer, 'customerId' | 'phoneNumber'>>;
  enabled?: boolean;
}) {
  const { user } = useUser();

  const { customerId, phoneNumber } = customer;

  const { isPhoneUsable, normalizedPhone } = React.useMemo(() => {
    const usable = testPhoneNumbervalue(phoneNumber);
    return {
      isPhoneUsable: usable,
      normalizedPhone: usable ? toE164(phoneNumber) : phoneNumber,
    };
  }, [phoneNumber]);

  const shouldFetch =
    enabled && !!user?.business?.id && (!!customerId || isPhoneUsable);

  return useQuery({
    queryKey: [
      'customer-gift-card-balance',
      {
        businessId: user?.business?.id,
        customerId,
        phoneNumber: normalizedPhone,
      },
    ],
    queryFn: async () => {
      if (!user?.business?.id) {
        throw new Error('Business ID is required');
      }
      return getCustomerGiftCardBalance({
        businessId: user.business.id,
        customerId,
        phoneNumber: normalizedPhone,
      });
    },
    enabled: shouldFetch,
    staleTime: 60_000,
    gcTime: 60_000,
  });
}

export default useGetCustomerGiftCardBalance;
